import { Suspense, type ReactNode } from 'react';
import { Route, Navigate } from 'react-router-dom';
import { resolvePageComponent } from './pageRegistry.js';
import type { PageSpec } from '@elmes/shared/plugin/manifest';
import { logger } from '../lib/logger.js';

interface PluginEntry {
  slug: string;
  pages: PageSpec[];
}

function SuspenseFallback() {
  return <div className="animate-pulse p-4 text-gray-400">Yükleniyor...</div>;
}

function wrapSuspense(Comp: React.ComponentType<any>): ReactNode {
  return (
    <Suspense fallback={<SuspenseFallback />}>
      <Comp />
    </Suspense>
  );
}

/**
 * Build React Router <Route> elements from a plugin's pages array.
 *
 * Strategy (Faz 6 #183, updated Faz 4 hotfix):
 *   - A page is a "layout candidate" if ALL of the following hold:
 *     1. Its component name ends with "Layout" (prevents ChatPage from being
 *        mistaken as a layout for /chat/admin children — Faz 4 hotfix).
 *     2. There are other pages whose paths start with this page's path + '/'.
 *     3. At least one such child has a different component (avoids treating
 *        /notes as a layout of /notes/:id when both use NotesPage).
 *   - Layout candidates are rendered as nested <Route> groups; their direct
 *     children are rendered inside them.
 *   - Pages that are already consumed as layout children are not rendered again.
 *   - All remaining pages are rendered as flat top-level <Route> elements.
 *
 * This supports mixed plugins that have both flat pages and nested layout
 * groups (e.g. chat plugin: /chat flat + /chat/admin layout group).
 */
export function buildPluginRoutes(plugins: PluginEntry[]): ReactNode[] {
  const routes: ReactNode[] = [];

  for (const plugin of plugins) {
    if (!plugin.pages || plugin.pages.length === 0) continue;

    // Track pages consumed as children of a layout so they are not double-rendered
    const consumed = new Set<PageSpec>();

    // First pass: identify and render layout groups
    for (const candidate of plugin.pages) {
      // Only *Layout components qualify as layout containers (Faz 4 hotfix)
      if (!candidate.component.endsWith('Layout')) continue;

      const childPages = plugin.pages.filter(
        (other) =>
          other !== candidate && other.path.startsWith(candidate.path + '/'),
      );

      // Must have at least one child with a different component
      if (
        childPages.length === 0 ||
        !childPages.some((child) => child.component !== candidate.component)
      ) {
        continue;
      }

      const LayoutComp = resolvePageComponent(candidate.component);
      if (!LayoutComp) {
        logger.warn(
          { slug: plugin.slug, component: candidate.component },
          '[plugin-routes] missing layout component',
        );
        continue;
      }

      // Mark candidate and its direct children as consumed
      consumed.add(candidate);
      for (const child of childPages) consumed.add(child);

      routes.push(
        <Route
          key={`${plugin.slug}:${candidate.path}:layout`}
          path={candidate.path}
          element={wrapSuspense(LayoutComp)}
        >
          {/* Default redirect: navigate to first child page */}
          <Route
            index
            element={<Navigate to={childPages[0]?.path ?? candidate.path} replace />}
          />
          {childPages.map((page) => {
            const Comp = resolvePageComponent(page.component);
            if (!Comp) {
              logger.warn(
                { slug: plugin.slug, component: page.component },
                '[plugin-routes] missing component (child)',
              );
              return null;
            }
            // Child path relative to layout path (strip layout prefix + leading slash)
            const relativePath = page.path.slice(candidate.path.length + 1);
            return (
              <Route
                key={`${plugin.slug}:${page.path}`}
                path={relativePath}
                element={wrapSuspense(Comp)}
              />
            );
          })}
        </Route>,
      );
    }

    // Second pass: render remaining (non-consumed) pages as flat routes
    for (const page of plugin.pages) {
      if (consumed.has(page)) continue;

      const Comp = resolvePageComponent(page.component);
      if (!Comp) {
        logger.warn(
          { slug: plugin.slug, component: page.component },
          '[plugin-routes] missing component (flat)',
        );
        continue;
      }
      routes.push(
        <Route
          key={`${plugin.slug}:${page.path}`}
          path={page.path}
          element={wrapSuspense(Comp)}
        />,
      );
    }
  }

  return routes;
}
